import './index.scss';

import React from 'react';
import Scrollbar from 'react-scrollbars-custom';
import { connect } from 'react-redux';
import { Layout, Divider, Skeleton, Button } from 'antd';
import { format as formatDate } from 'date-fns';
import { CalendarOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { User } from '../../types/user';
import getNested from '../../utils/getNested';
import { getUserFullName } from '../../utils/bookingUtil';

interface Props {
  skeleton?: boolean;
  isAdmin: boolean;
  salonId: number;
  accessToken: string;
  users: User[];
  primaryColor: string;

  // only show view profile on booking detail
  canViewProfile?: boolean;

  isFetchingProfile?: boolean;
  fetchUserProfile?: (options) => Promise<any>;
}

class BookingSummary extends React.PureComponent<Props> {
  private onClickiewUserProfile(profileId: number) {
    const { salonId, accessToken, fetchUserProfile } = this.props;

    if (salonId && accessToken && fetchUserProfile) {
      const options = { salonId, profileId, accessToken };
      fetchUserProfile(options);
    }
  }

  private renderViewProfile(user: User) {
    const { isAdmin, isFetchingProfile, canViewProfile: isViewProfileVisible } = this.props;
    const { profileId, canViewProfile } = user;

    if (isAdmin && profileId && canViewProfile && isViewProfileVisible) {
      return (
        <Button
          size="small"
          shape="round"
          type="primary"
          disabled={isFetchingProfile}
          onClick={() => this.onClickiewUserProfile(profileId)}
          style={{ marginLeft: 20 }}
        >View details</Button>
      );
    }

    return null;
  }

  getDateTime() {
    const { users } = this.props;
    const { selectedDate, selectedTime } = (users || [])[0];

    return {
      date: selectedDate ? formatDate(selectedDate, 'EEEE, MMM dd yyyy') : undefined,
      time: selectedTime,
    };
  }

  renderUser(user: User, index: number) {
    const { skeleton, users, primaryColor } = this.props;
    const userServices = getNested(user, 'selectedServices', []);

    return (
      <div key={`booking-summary--user-${user.id}-${Math.random()}`}>
        {index > 0 && <Divider />}

        <div className="booking-summary--user" style={{ minHeight: 60 }}>
          <h3 style={{ marginBottom: 0, display: 'flex', alignItems: 'center' }}>
            {users.length > 1 ? `${index + 1}. ` : ''} {getUserFullName(user)} {this.renderViewProfile(user)}
          </h3>

          {!skeleton && userServices.map(service => (
            <div
              key={`user-${user.id}-service-${service.id}-${Math.random()}`}
              style={{
                justifyContent: 'space-between',
                color: primaryColor,
                display: 'flex',
                paddingTop: 6,
              }}
            >
              <span>{service.name}</span>
              <span>{user.canSelectStylist ? service.stylistName : 'No Preference'}</span>
            </div>
          ))}

          {skeleton && [1, 2].map(() => (
            <div
              key={Math.random()}
              style={{
                justifyContent: 'space-between',
                display: 'flex',
                paddingTop: 10,
              }}
            >
              <div style={{ width: '60%', height: 20, overflow: 'hidden' }}><Skeleton.Input size="small" /></div>
              <div style={{ width: '30%', height: 20, overflow: 'hidden' }}><Skeleton.Input size="small" /></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  render() {
    const { skeleton, users, primaryColor } = this.props;
    const dateTime = this.getDateTime();

    return (
      <Layout className="fave-layout fave-block fave-block--booking-summary">
        <Layout.Header className="fave-block--header">
          Booking Summary
        </Layout.Header>

        <Layout.Content
          className="fave-block--content"
          style={{ padding: 0, position: 'relative' }}
        >
          <Layout className="fave-layout">
            <Layout.Header
              className="fave-layout--header"
              style={{
                height: 'auto',
                paddingBottom: 16,
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              {skeleton && (
                <>
                  <div className="booking-item" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 10,
                  }}>
                    <div style={{ width: 30, height: 20, overflow: 'hidden' }}><CalendarOutlined /></div>
                    <div style={{ flexShrink: 1, flexGrow: 1, height: 20, overflow: 'hidden' }}><Skeleton.Input size="small" /></div>
                  </div>
                  <div className="booking-item" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}>
                    <div style={{ width: 30, height: 20, overflow: 'hidden' }}><ClockCircleOutlined /></div>
                    <div style={{ flexShrink: 1, flexGrow: 1, height: 20, overflow: 'hidden' }}><Skeleton.Input size="small" /></div>
                  </div>
                </>
              )}

              {!skeleton && (
                <>
                  <div className="booking-item" style={{ marginBottom: 6 }}>
                    <div className="booking-item-label">
                      <CalendarOutlined style={{ marginRight: 4 }} /> Date
                    </div>
                    <div className="booking-item-value" style={{ color: primaryColor }}>
                      {dateTime.date}
                    </div>
                  </div>

                  <div className="booking-item">
                    <div className="booking-item-label">
                      <ClockCircleOutlined style={{ marginRight: 4 }} /> Time
                    </div>
                    <div className="booking-item-value" style={{ color: primaryColor }}>{dateTime.time}</div>
                  </div>
                </>
              )}
            </Layout.Header>
            <Layout.Content className="fave-block--content">
              <Scrollbar>
                <div className="fave-block--booking-summary--content">
                  {users.map((user, index) => this.renderUser(user, index))}
                </div>
              </Scrollbar>
            </Layout.Content>
          </Layout>
        </Layout.Content>
      </Layout>
    );
  }
}

const mapStateToProps = state => ({
  isAdmin: state.salonStore.isAdmin,
  salonId: state.salonStore.salonId,
  accessToken: state.salonStore.accessToken,
  users: state.bookingStore.users,
  primaryColor: state.salonStore.primaryColor,
});

export default connect(mapStateToProps)(BookingSummary);
